/*
 * Copyright (c) 2022 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Sunday, August 21st 2022, 5:56:49 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import List from "@mui/material/List";
import Stack from "@mui/material/Stack";
import { TaskTemplate, NextActivation } from "lib/schemas";
import { getNewActivationDefault } from "lib/factories";
import { APIOne2ManyListEditorFormInput } from "ui-widgets/forms";
import { APISERVICEPATHS } from "lib/api";
import { useOSFormContext } from "ui-widgets/forms/providers";
import { OSActionButton, useToggleState } from "ui-widgets";
import { ActivationBranchForm } from "./ActivationBranchForm";
import { ActivationBranchListItem } from "./ActivationBranchListItem";

interface ActivationBranchListProps {
  onSaveActivationBranchCallback: (savedActivation: NextActivation) => void;
  onDeleteActivationBranchCallback: (deletedActivation: NextActivation) => void;
}
export const ActivationBranchList = ({
  onSaveActivationBranchCallback,
  onDeleteActivationBranchCallback,
}: ActivationBranchListProps) => {
  const { watchedTaskTemplateId, getNewActivation } = useActivationBranchListLogic();

  const { handleClose, handleOpen, open } = useToggleState();

  return (
    <>
      <APIOne2ManyListEditorFormInput<TaskTemplate, NextActivation>
        apiServicePath={APISERVICEPATHS.nextActivations}
        foreignKey="task_definition"
        foreignKeyId={watchedTaskTemplateId}
        uniqueIdentifier="id"
        renderListView={({
          itemsListState: {
            savedItems: activationBranches,
            selectedItemId: selectedActivationBranchId,
            itemCreationQueue,
          },
          updateListAndSelectIfNewItem: updateActivationBranchList,
          createNewItem: createNewActivationBranch,
          selectItem: selectActivationBranch,
          findItem: findActivationBranch,
        }) => {
          const onClickAddActivationBranch = () => {
            createNewActivationBranch(getNewActivation());
            selectActivationBranch(null);
            handleOpen();
          };

          const onClickEditActivationBranch = (activationBranchId: NextActivation["id"]) => {
            selectActivationBranch(activationBranchId);
            handleOpen();
          };

          const getActivationBranchToEdit = (): NextActivation => {
            return selectedActivationBranchId
              ? findActivationBranch(selectedActivationBranchId).item
              : itemCreationQueue[0];
          };

          const onSaveActivationBranch = (nextActivation: NextActivation) => {
            updateActivationBranchList(nextActivation);
            onSaveActivationBranchCallback(nextActivation);
          };

          return (
            <>
              <List sx={{ width: "100%", bgcolor: "background.paper" }}>
                <Stack spacing={2}>
                  {activationBranches.map((activationBranch) => (
                    <ActivationBranchListItem
                      key={activationBranch.id}
                      activationBranch={activationBranch}
                      onClickEditActivationBranch={onClickEditActivationBranch}
                      onDeleteActivationBranchCallback={onDeleteActivationBranchCallback}
                    />
                  ))}
                </Stack>
                <ActivationBranchForm
                  nextActivationFetchId={null}
                  nextActivationDefaultValues={getActivationBranchToEdit()}
                  updateNextActivationFromAPICallback={onSaveActivationBranch}
                  onSuccessfulAPISave={null}
                  open={open}
                  handleClose={handleClose}
                />
              </List>
              <OSActionButton actionStyle="add" onClick={onClickAddActivationBranch}>
                Add Outgoing Branch
              </OSActionButton>
            </>
          );
        }}
      />
    </>
  );
};

const useActivationBranchListLogic = () => {
  const { watch } = useOSFormContext<TaskTemplate>();

  const [watchedTaskTemplateId, watchedTaskTemplateOrgId] = watch<(keyof TaskTemplate)[]>(["id", "artifact_org"]);

  const getNewActivation = () =>
    getNewActivationDefault({
      organizationId: watchedTaskTemplateOrgId as string,
      taskTemplateId: watchedTaskTemplateId as number,
    });

  return { watchedTaskTemplateId, getNewActivation };
};
